import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  clearBufferReport,
  hardReset,
  processData,
  processList,
  realtimeCountGet,
  realtimeListGet,
  realtimeLiveDataGet
} from '../../../store/slices/tenantSlices/realtimeReportSlice';
import { Button, Card, Col, Row } from 'reactstrap';
import FormSelect from '../../../components/form/FormSelect';
import { refreshTimeOptions } from '../../../utils/common';
import ProcessDetails from './ProcessDetails';
import ReportCount from './ReportCount';
import ReportList from './ReportList';
import ReportLiveData from './ReportLiveData';
import AdminHeader from '../../../components/header/AdminHeader';
import { toast } from 'react-toastify';
import ClearBufferConfirmationModal from './ClearBufferConfirmationModal';
import HardResetConfirmationModal from './HardResetConfirmationModal';
import { useUserDetails } from '../../../store/slices/authSlice';
import RecentCallModal from '../Configuration/RecentCallModal';

const RealtimeReport = () => {
  const user = useUserDetails();

  const dispatch = useDispatch();

  const [processId, setProcessId] = useState('');
  const [hyperProcessId, setHyperProcessId] = useState('');
  const [refreshTime, setRefreshTime] = useState(5);
  const [processOptions, setProcessOptions] = useState([]);
  const [proData, setProData] = useState([]);
  const [countData, setCountData] = useState([]);
  const [listData, setListData] = useState([]);
  const [liveData, setLiveData] = useState({ ringing_data: [], waiting_data: [] });
  const [refresh, setRefresh] = useState();
  const [bufferClearModalOpen, setBufferClearModalOpen] = useState(false);
  const [recentCallModalOpen, setRecentCallModalOpen] = useState(false);
  const [hardResetModalOpen, setHardResetModalOpen] = useState(false);

  const getProcessList = async () => {
    try {
      const response = await dispatch(processList()).unwrap();
      const options =
        response && response.data.length > 0
          ? response.data.map((item) => ({ label: item.name, value: item.id }))
          : [];
      setProcessOptions([...(options || [])]);
    } catch (error) {
      console.log('error', error);
    }
  };

  const getProcessData = async () => {
    try {
      const payload = {
        selected_process_id: processId || undefined
      };
      const response = await dispatch(processData(payload)).unwrap();
      if (response && response?.data && response?.data?.length > 0) {
        const sortedData = response.data.sort((a, b) => {
          return (
            (a.process_status_final === 'true' ? -1 : 1) -
            (b.process_status_final === 'true' ? -1 : 1)
          );
        });
        setProData(sortedData);
      } else {
        setProData([]);
      }
    } catch (error) {
      setProData([]);
      console.log('error', error);
    }
  };

  const getCountData = async (process_id) => {
    try {
      const payload = {
        selected_process_id: process_id || undefined
      };
      const response = await dispatch(realtimeCountGet(payload)).unwrap();
      if (response && response?.data && response?.data?.length > 0) {
        setCountData(response.data);
      } else {
        setCountData([]);
      }
    } catch (error) {
      setCountData([]);
      console.log('error', error);
    }
  };

  const getListData = async (process_id) => {
    try {
      const payload = {
        selected_process_id: process_id || undefined
      };
      const response = await dispatch(realtimeListGet(payload)).unwrap();
      if (response && response?.data && response?.data?.length > 0) {
        setListData(response.data);
      } else {
        setListData([]);
      }
    } catch (error) {
      setListData([]);
      console.log('error', error);
    }
  };

  const getLiveReportData = async (process_id) => {
    try {
      const payload = {
        selected_process_id: process_id || undefined
      };
      const response = await dispatch(realtimeLiveDataGet(payload)).unwrap();
      if (response && response?.success) {
        setLiveData({
          ringing_data: response.ringing_data?.length ? response.ringing_data : [],
          waiting_data: response.waiting_data?.length ? response.waiting_data : []
        });
      } else {
        setLiveData([]);
      }
    } catch (error) {
      setLiveData([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getProcessList();
  }, []);

  useEffect(() => {
    const fetchData = () => {
      const id = hyperProcessId ? hyperProcessId : processId;
      getLiveReportData(id);
      getProcessData();
      getCountData(id);
      getListData(id);
    };

    fetchData();

    if (refreshTime) {
      const intervalId = setInterval(fetchData, refreshTime * 1000);

      return () => clearInterval(intervalId);
    }
  }, [refreshTime, processId, hyperProcessId, refresh]);

  const onBufferClear = async () => {
    try {
      const response = await dispatch(
        clearBufferReport({
          selected_process_id: hyperProcessId ? hyperProcessId : processId ? processId : undefined
        })
      ).unwrap();
      toast.success(response?.message);
      setBufferClearModalOpen(false);
    } catch (error) {
      console.log('error', error);
    }
  };

  const onHardReset = async () => {
    try {
      const response = await dispatch(hardReset()).unwrap();
      toast.success(response?.message);
      setHardResetModalOpen(false);
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <Fragment>
      <AdminHeader title="Realtime Report" />
      <Row>
        <Col lg="2" md="2" className="px-2">
          <Card className="mb-3 px-2 py-1">
            <Row>
              <Col lg="12" md="12">
                <FormSelect
                  isShowLabel={false}
                  name="processId"
                  value={hyperProcessId ? hyperProcessId : processId}
                  placeholder="Select Process"
                  options={[{ label: 'All', value: '' }, ...processOptions]}
                  onChange={(e) => {
                    setProcessId(e.target.value);
                    setHyperProcessId('');
                  }}
                />
              </Col>
              {user?.type !== 'supervisor' ? (
                <Col lg="12" md="12">
                  <Button
                    color="primary"
                    className="px-4 w-100"
                    type="submit"
                    onClick={() => setHardResetModalOpen(true)}>
                    Hard Reset
                  </Button>
                </Col>
              ) : null}
            </Row>
          </Card>
          <Card className="m-0">
            <ProcessDetails
              data={proData}
              setHyperProcessId={setHyperProcessId}
              activeId={hyperProcessId ? hyperProcessId : processId}
            />
          </Card>
        </Col>
        <Col lg="8" md="8" className="px-2">
          <Card className="p-2 m-0">
            <ReportCount data={countData} />
            <ReportList
              data={listData}
              setRefresh={setRefresh}
              setHyperProcessId={setHyperProcessId}
            />
          </Card>
        </Col>
        <Col lg="2" md="2" className="px-2">
          <Card className="mb-3 px-2 py-2">
            <Row>
              <Col lg="12" md="12">
                <FormSelect
                  isShowLabel={false}
                  name="refreshTime"
                  value={refreshTime}
                  placeholder="Select Refresh"
                  options={refreshTimeOptions}
                  onChange={(e) => setRefreshTime(e.target.value)}
                />
              </Col>
              <Col lg="12" md="12" className="mb-2">
                <Button
                  color="primary"
                  className="px-4 w-100"
                  type="submit"
                  onClick={() => setBufferClearModalOpen(true)}>
                  Clear Buffer
                </Button>
              </Col>
              <Col lg="12" md="12">
                <Button
                  color="violet"
                  className="px-4 w-100"
                  type="submit"
                  onClick={() => setRecentCallModalOpen(true)}>
                  Recent Call
                </Button>
              </Col>
            </Row>
          </Card>
          <div className="live-data-container">
            <Card className="m-0">
              <ReportLiveData data={liveData?.ringing_data} title="Ringing" />
            </Card>
            <Card className="m-0">
              <ReportLiveData data={liveData?.waiting_data} title="Waiting" />
            </Card>
          </div>
        </Col>
      </Row>
      <ClearBufferConfirmationModal
        isOpen={bufferClearModalOpen}
        onClearClick={() => onBufferClear()}
        onCloseClick={() => setBufferClearModalOpen(false)}
      />
      <HardResetConfirmationModal
        isOpen={hardResetModalOpen}
        onResetClick={() => onHardReset()}
        onCloseClick={() => setHardResetModalOpen(false)}
      />
      <RecentCallModal
        isOpen={recentCallModalOpen}
        processOptions={processOptions}
        onCloseClick={() => setRecentCallModalOpen(false)}
      />
    </Fragment>
  );
};

export default RealtimeReport;
